import type { FocusOrigin } from '@angular/cdk/a11y';
import { computed, Directive, input, type TemplateRef } from '@angular/core';

import { injectPopoverTriggerState, NgpPopover, NgpPopoverTrigger } from 'ng-primitives/popover';

import { twMerge } from '@shared/tw-merge';

import type { PopoverPlacement } from './popover.types';
import { popoverVariants } from './popover.variants';

@Directive({
  selector: 'button[ksPopoverTrigger]',
  hostDirectives: [
    {
      directive: NgpPopoverTrigger,
      inputs: [
        'ngpPopoverTrigger: ksPopoverTrigger',
        'ngpPopoverTriggerPlacement: ksPopoverPlacement',
        'ngpPopoverTriggerContext: ksPopoverData',
        'ngpPopoverTriggerDisabled: ksPopoverDisabled',
        'ngpPopoverTriggerCloseOnEscape: ksPopoverCloseOnEscape',
        'ngpPopoverTriggerCloseOnOutsideClick: ksPopoverCloseOnOutsideClick',
      ],
      outputs: ['ngpPopoverTriggerOpenChange: ksPopoverOpenChange'],
    },
  ],
  exportAs: 'ksPopoverTrigger',
})
export class PopoverTriggerDirective<T> {
  readonly trigger = input.required<TemplateRef<unknown>>({ alias: 'ksPopoverTrigger' });
  readonly placement = input<PopoverPlacement>('bottom', { alias: 'ksPopoverPlacement' });
  readonly data = input<T>(undefined, { alias: 'ksPopoverData' });
  readonly disabled = input<boolean>(false, { alias: 'ksPopoverDisabled' });
  readonly closeOnEscape = input<boolean>(true, { alias: 'ksPopoverCloseOnEscape' });
  readonly closeOnOutsideClick = input<boolean>(false, { alias: 'ksPopoverCloseOnOutsideClick' });

  private readonly popoverTriggerState = injectPopoverTriggerState();

  readonly opened = this.popoverTriggerState().open;

  open(): void {
    void this.popoverTriggerState().show();
  }

  close(origin?: FocusOrigin): void {
    void this.popoverTriggerState().hide(origin ?? 'program');
  }
}

@Directive({
  selector: '[ksPopover]',
  hostDirectives: [NgpPopover],
  host: { '[class]': 'hostClass()' },
})
export class PopoverDirective {
  readonly extraClass = input<string>('', { alias: 'class' });

  protected readonly hostClass = computed(() => twMerge(popoverVariants, this.extraClass()));
}
